import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  TextField,
  MenuItem,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { authGet, authPost, authPatch, authDelete } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { formatDateTime } from '../../lib/dateFormat';

export default function AdminRegistrationRequestsPage() {
  const currentUser = getUser();

  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [notesById, setNotesById] = useState({});
  const [roleById, setRoleById] = useState({});

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'user',
    organization_name: ''
  });

  const [editUser, setEditUser] = useState({
    id: '',
    full_name: '',
    email: '',
    role: 'user',
    organization_name: ''
  });

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [requestsData, usersData] = await Promise.all([
        authGet('/api/admin/registration-requests'),
        authGet('/api/admin/users')
      ]);

      setRequests(requestsData.requests || []);
      setUsers(usersData.users || []);
    } catch (err) {
      setError(err.message || 'Unable to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleApprove = async (id) => {
    setError(null);
    setSuccess(null);

    try {
      await authPost(`/api/admin/registration-requests/${id}/approve`, {
        role: roleById[id] || 'user',
      });

      setSuccess('Registration request approved');
      await loadData();
    } catch (err) {
      setError(err.message || 'Unable to approve request');
    }
  };

  const handleReject = async (id) => {
    setError(null);
    setSuccess(null);

    try {
      await authPost(`/api/admin/registration-requests/${id}/reject`, {
        notes: notesById[id] || '',
      });

      setSuccess('Registration request rejected');
      await loadData();
    } catch (err) {
      setError(err.message || 'Unable to reject request');
    }
  };

  const handleCreateUser = async () => {
    setError(null);
    setSuccess(null);

    try {
      await authPost('/api/admin/users', newUser);

      setSuccess('User created successfully');
      setAddUserOpen(false);
      setNewUser({
        full_name: '',
        email: '',
        password: '',
        role: 'user',
        organization_name: ''
      });

      await loadData();
    } catch (err) {
      setError(err.message || 'Unable to create user');
    }
  };

  const openEditDialog = (user) => {
    setEditUser({
      id: user.id,
      full_name: user.full_name || '',
      email: user.email || '',
      role: user.role || 'user',
      organization_name: user.organization_name || ''
    });
    setEditUserOpen(true);
  };

  const handleUpdateUser = async () => {
    setError(null);
    setSuccess(null);

    try {
      await authPatch(`/api/admin/users/${editUser.id}`, {
        full_name: editUser.full_name,
        role: editUser.role,
        organization_name: editUser.organization_name
      });

      setSuccess('User updated successfully');
      setEditUserOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message || 'Unable to update user');
    }
  };

  const handleDeleteUser = async (id) => {
    setError(null);
    setSuccess(null);

    if (!window.confirm('Delete this user?')) return;

    try {
      await authDelete(`/api/admin/users/${id}`);
      setSuccess('User deleted successfully');
      await loadData();
    } catch (err) {
      setError(err.message || 'Unable to delete user');
    }
  };

  const handleResetPassword = async () => {
    setError(null);
    setSuccess(null);

    try {
      await authPost(`/api/admin/users/${resetUserId}/reset-password`, {
        password: newPassword,
      });

      setSuccess('Password reset successfully');
      setResetOpen(false);
      setResetUserId(null);
      setNewPassword('');
    } catch (err) {
      setError(err.message || 'Unable to reset password');
    }
  };

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="h3" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body1">
            Review requests and manage users.
          </Typography>
        </Box>

        <Button variant="contained" onClick={() => setAddUserOpen(true)}>
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading && <Typography>Loading...</Typography>}

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Existing Users
        </Typography>

        <Grid container spacing={2}>
          {users.map((user) => (
            <Grid item xs={12} key={user.id}>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                  <Box>
                    <Typography variant="h6">{user.full_name || 'Unnamed User'}</Typography>
                    <Typography variant="body2">{user.email}</Typography>
                    <Typography variant="body2">Role: {user.role}</Typography>
                    <Typography variant="body2">
                      Organization: {user.organization_name || '—'}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setResetUserId(user.id);
                        setResetOpen(true);
                      }}
                    >
                      Reset Password
                    </Button>

                    <Button variant="outlined" onClick={() => openEditDialog(user)}>
                      Edit
                    </Button>

                    {user.id !== currentUser?.id && (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Registration Requests
        </Typography>

        {!loading && requests.length === 0 && !error && (
          <Paper sx={{ p: 3, mt: 2 }} variant="outlined">
            <Typography>No registration requests found.</Typography>
          </Paper>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {requests.map((request) => (
            <Grid item xs={12} key={request.id}>
              <Paper sx={{ p: 3 }} variant="outlined">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  gap={2}
                  flexWrap="wrap"
                >
                  <Box>
                    <Typography variant="h6">
                      {request.first_name} {request.last_name}
                    </Typography>
                    <Typography variant="body2">{request.email}</Typography>
                  </Box>

                  <Chip
                    label={request.status}
                    color={
                      request.status === 'pending'
                        ? 'warning'
                        : request.status === 'approved'
                        ? 'success'
                        : request.status === 'rejected'
                        ? 'error'
                        : 'default'
                    }
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography><strong>Phone:</strong> {request.phone || '—'}</Typography>
                    <Typography><strong>Company Role:</strong> {request.company_role || '—'}</Typography>
                    <Typography><strong>Organization:</strong> {request.org_name || '—'}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography><strong>Submitted:</strong> {formatDateTime(request.created_at)}</Typography>
                    <Typography><strong>Reviewed:</strong> {formatDateTime(request.reviewed_at, { fallback: '—' })}</Typography>
                    <Typography><strong>Notes:</strong> {request.notes || '—'}</Typography>
                  </Grid>
                </Grid>

                {request.status === 'pending' && (
                  <Box mt={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          label="Approve As"
                          value={roleById[request.id] || 'user'}
                          onChange={(e) =>
                            setRoleById((prev) => ({ ...prev, [request.id]: e.target.value }))
                          }
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label="Rejection Notes"
                          value={notesById[request.id] || ''}
                          onChange={(e) =>
                            setNotesById((prev) => ({ ...prev, [request.id]: e.target.value }))
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box display="flex" gap={2}>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleApprove(request.id)}
                          >
                            Approve
                          </Button>

                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleReject(request.id)}
                          >
                            Reject
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Full Name"
            value={newUser.full_name}
            onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Email"
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Temporary Password"
            type="password"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
          />

          <TextField
            select
            fullWidth
            margin="normal"
            label="Role"
            value={newUser.role}
            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="normal"
            label="Organization Name"
            value={newUser.organization_name}
            onChange={(e) => setNewUser({ ...newUser, organization_name: e.target.value })}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateUser}>
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editUserOpen} onClose={() => setEditUserOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Full Name"
            value={editUser.full_name}
            onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Email"
            value={editUser.email}
            disabled
          />

          <TextField
            select
            fullWidth
            margin="normal"
            label="Role"
            value={editUser.role}
            onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="normal"
            label="Organization Name"
            value={editUser.organization_name}
            onChange={(e) => setEditUser({ ...editUser, organization_name: e.target.value })}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditUserOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateUser}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setResetOpen(false);
              setResetUserId(null);
              setNewPassword('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleResetPassword}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}